import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAppSelector } from "@/state/hooks";
import { Button, Paper, Box, Card, Typography, IconButton, Skeleton, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { Refresh as RefreshIcon, Warning as WarningIcon } from "@mui/icons-material";
import { submitAndStreamJob } from "@/api/workProcessor";
import { deriveDocScope } from "./docScope";
import "./DocSection.scss";
import { Resizable } from "re-resizable";

// Admin
// PromptEditor has been removed - commented out
// import PromptEditor from "@/components/Admin/PromptEditor/PromptEditor";

//Redux
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "@/state/store";
import { WorkproductDocument, updateDocumentContent } from "@/api";
import { getWorkProductDocuments, resetDocumentsState } from "@/state/workProductsProcessor/WorkProductDocumentsSlice";
// Header removed - no longer needed
import TiptapEditor from "@/components/tiptap/TiptapEditor";
import type { TiptapCollab } from "@/components/tiptap/TiptapEditor";
import { useCollabSession } from "@/collab/CollabSessionContext";
import { sectionField } from "@/collab/roomName";
import { getHostDocumentBridge, renderMarkdownToHtml } from "@/components/aiBridge";
import {
    setActiveTab as setActiveTabRedux,
    setActiveEditorContent,
    selectActiveEditorContent,
    selectActiveTabIndex,
    selectActiveTabName,
    selectActiveTabSectionId,
    selectActiveContextKey,
    cancelPreview,
    makeSelectPreviewForSection
} from "@/state/activeEditor/activeEditorSlice";
import { setSectionContent, updateSectionContent, loadAllSections, selectAllSectionsArray } from "@/state/documentContext/documentContextSlice";
import ReactMarkdown from "react-markdown";
import { wouldErasePopulatedDocument } from "@/pages/interface/utils/documentContent";

// Removed - no longer needed as tabs are handled in parent component

interface WorkProduct_GetDocuments {
    status: "idle" | "loading" | "succeeded" | "failed";
    documents?: WorkproductDocument[] | null;
    error?: string | null;
}

const buildDocLatestVersionContextKey = (
    workProductId: number,
    projectId: number,
    featureId?: string | number,
    userStoryId?: string
): string => `doc-latest:${projectId || 0}:${featureId || ""}:${userStoryId || ""}:${workProductId}`;

// Removed - no longer needed as tabs are handled in parent component

export interface DocSectionContainerProps {
    workProduct?: any; // Work product passed from parent
    projectId?: number;
    featureId?: string;
    userStoryId?: string; // User story ID for filtering sections
    hasJiraLink?: boolean; // Whether the project has a Jira integration linked
    hasAzureDevOpsLink?: boolean; // Whether the project has an Azure DevOps integration linked
    activeTabIndex?: number; // Active tab index from parent
    activeSection?: any; // Active section from parent
    onRebuildComplete?: () => void; // Callback after rebuild is initiated (for navigation)
    onRebuildStart?: (workProductId: string, projectId?: number, featureId?: string | number, userStoryId?: string) => void; // Callback to set generating state
    onProgressUpdate?: (
        workProductId: string,
        projectId?: number,
        featureId?: string | number,
        userStoryId?: string,
        update?: { progress?: number; message?: string }
    ) => void; // Callback for progress updates
    onRebuildJobComplete?: (workProductId: string, projectId?: number, featureId?: string | number, userStoryId?: string) => void; // Callback when rebuild job completes
    /** Navigate to the Integrations view — wired to SyncPanel's "Reconnect" CTA (see Task 16). */
    onNavigateToIntegrations?: () => void;
    readOnly?: boolean;
    canRebuild?: boolean;
}

const DocSectionContainer: React.FC<DocSectionContainerProps> = ({
    workProduct,
    projectId,
    featureId,
    userStoryId,
    hasJiraLink,
    hasAzureDevOpsLink,
    activeTabIndex = 0,
    activeSection,
    onRebuildComplete,
    onRebuildStart,
    onProgressUpdate,
    onRebuildJobComplete,
    onNavigateToIntegrations,
    readOnly = true,
    canRebuild = false
}) => {
    // Use activeTabIndex from props instead of local state
    const activeTab = activeTabIndex;
    // Rebuild confirmation dialog state
    const [showRebuildDialog, setShowRebuildDialog] = useState(false);
    const [isRebuilding, setIsRebuilding] = useState(false);
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const writeAllowedRef = useRef(!readOnly);
    writeAllowedRef.current = !readOnly;
    useEffect(() => {
        if (readOnly) {
            setShowRebuildDialog(false);
            setIsContentModified(false);
        }
    }, [readOnly]);
    const userProfile = useAppSelector(state => state.userProfiles.userProfile);
    const token = useAppSelector(state => state.clientToken.token);
    const activeEditorContent = useAppSelector(selectActiveEditorContent);
    const activeTabIndexFromRedux = useAppSelector(selectActiveTabIndex);
    const activeTabNameFromRedux = useAppSelector(selectActiveTabName);
    const activeTabSectionIdFromRedux = useAppSelector(selectActiveTabSectionId);
    const activeContextKeyFromRedux = useAppSelector(selectActiveContextKey);
    const allSections = useAppSelector(selectAllSectionsArray);

    const [masterSettings, setMasterSettings] = useState<any>(null); // Placeholder for master settings
    // Track accepted content per tab

    type PerWpTabs = Record<number, Record<number, string>>;

    const [tabContents, setTabContents] = useState<PerWpTabs>({});
    const [originalContent, setOriginalContent] = useState<PerWpTabs>({});

    // Force re-render when content changes
    const [contentVersion, setContentVersion] = useState(0);
    const [adminEditorActive, setAdminEditorActive] = useState<boolean>(false);
    const [sortedDocuments, setSortedDocuments] = useState<WorkproductDocument[]>([]);
    const [isContentModified, setIsContentModified] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    // Bumped when a `storyContentUpdated` window event fires for this container's
    // userStoryId (e.g. after a sync pull from Jira/Azure DevOps). Included in the
    // section-loading effect's deps so a pull refreshes the editor content (Task 16).
    const [refreshNonce, setRefreshNonce] = useState(0);

    // Check URL parameters for Admin=true
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const adminParam = urlParams.get("Admin");
        setAdminEditorActive(adminParam === "true");
    }, []);

    // Redux
    const dispatch = useDispatch<AppDispatch>();
    const [sectionHeader, setSectionHeader] = useState<string>();
    const [sectionDescription, setSectionDescription] = useState<string>();
    const { status, error, documents, activeContext } = useSelector((state: RootState) => state.workProductDocuments);
    const wpTitle = useSelector((state: RootState) => state.workProductDocuments.wpTitle);
    const [topHeight, setTopHeight] = useState(200); // Prompt Sizer ADMIN ONLY
    const [currentWorkProductId, setCurrentWorkProductId] = useState<number | null>(null);
    const [isLoadingLatestRegeneratedVersion, setIsLoadingLatestRegeneratedVersion] = useState(false);

    const settings = useSelector((state: RootState) => state.userProfiles.userProfile?.settings);
    // Always use work product ID from prop when available - with proper validation
    const wpId = (() => {
        if (workProduct?.id) {
            const parsed = typeof workProduct.id === "string" ? parseInt(workProduct.id, 10) : workProduct.id;
            // Ensure parsed value is a valid number (not NaN)
            if (!isNaN(parsed) && parsed > 0) {
                return parsed;
            }
        }
        // Fallback to settings
        const fallback = settings?.ActiveWorkProductId;
        return typeof fallback === "number" && !isNaN(fallback) && fallback > 0 ? fallback : 0;
    })();
    const actualProjectId = (() => {
        const pid = projectId || settings?.ActiveProjectId || 0;
        return typeof pid === "number" && !isNaN(pid) && pid > 0 ? pid : 0;
    })();
    const scope = useMemo(
        () => deriveDocScope(workProduct, featureId, userStoryId),
        [featureId, userStoryId, workProduct?.level, workProduct?.user_story_id]
    );

    const actualFeatureId = scope.featureId;
    const actualUserStoryId = scope.userStoryId;
    // Composite key that uniquely identifies the current editing context
    // (project + feature + userStory + workProduct). Used to scope previews.
    const contextKey = useMemo(
        () => `${actualProjectId}-${actualFeatureId}-${actualUserStoryId || ""}-${wpId}`,
        [actualProjectId, actualFeatureId, actualUserStoryId, wpId]
    );
    const latestVersionContextKey = useMemo(
        () => buildDocLatestVersionContextKey(wpId, actualProjectId, actualFeatureId, actualUserStoryId),
        [wpId, actualProjectId, actualFeatureId, actualUserStoryId]
    );
    // derive once
    const activeSectionKey = sortedDocuments?.[activeTab]?.section_id;
    const sectionIdKey = activeSectionKey != null ? String(activeSectionKey) : null;

    // select from the Record by key
    const sectionFromCtx = useAppSelector((state: RootState) => (sectionIdKey ? state.documentContext.sections[sectionIdKey] : undefined));

    // Real-time collaboration session (null unless VITE_COLLAB_ENABLED and connected).
    const collabSession = useCollabSession();
    const activeDoc = sortedDocuments?.[activeTab];
    const [readOnlyCollabReady, setReadOnlyCollabReady] = useState(false);
    useEffect(() => {
        if (!readOnly) {
            setReadOnlyCollabReady(true);
            return;
        }
        if (!collabSession || !activeDoc?.warehouse_id || !collabSession.everSynced) {
            setReadOnlyCollabReady(false);
            return;
        }
        const fragment = collabSession.ydoc.getXmlFragment(sectionField(activeDoc.warehouse_id));
        const updateReady = () => setReadOnlyCollabReady(fragment.length > 0);
        updateReady();
        fragment.observeDeep(updateReady);
        return () => fragment.unobserveDeep(updateReady);
    }, [activeDoc?.warehouse_id, collabSession, readOnly]);
    // Until the first sync completes the editor stays in solo mode (today's HTML +
    // autosave) — this is the spec's server-unreachable fallback; after first sync
    // the binding survives transient disconnects (Yjs buffers offline edits).
    const collabForEditor: TiptapCollab | undefined = useMemo(() => {
        if (!collabSession || !activeDoc?.warehouse_id || !collabSession.everSynced || (readOnly && !readOnlyCollabReady)) return undefined;
        const field = sectionField(activeDoc.warehouse_id);
        return {
            fragment: collabSession.ydoc.getXmlFragment(field),
            field,
            provider: collabSession.provider,
            synced: collabSession.synced
        };
    }, [collabSession, activeDoc?.warehouse_id, readOnly, readOnlyCollabReady]);

    const derivedEditorValue = tabContents[wpId]?.[activeTab] ?? sectionFromCtx?.markdown ?? sortedDocuments?.[activeTab]?.content ?? "";

    // Look up the preview for this specific section from the multi-preview map
    const previewSelector = useMemo(
        () => makeSelectPreviewForSection(contextKey, sectionIdKey || ""),
        [contextKey, sectionIdKey]
    );
    const currentPreview = useAppSelector(previewSelector);
    const showPreviewForSection = currentPreview != null;
    const previewMarkdown = currentPreview?.markdown ?? null;
    const previewContent = currentPreview?.content ?? null;
    const isStreaming = currentPreview?.isStreaming ?? false;

    // Clear all content when work product changes - only depend on work product ID
    useEffect(() => {
        if (workProduct?.id !== currentWorkProductId) {
            // Detect if this is a component remount (returning to same WP) vs actual WP change
            const isComponentRemount = currentWorkProductId === null;
            const isActualWorkProductChange = !isComponentRemount && workProduct?.id !== currentWorkProductId;

            // On remount, use Redux's activeContextKey to detect if the context actually changed
            // (component local state resets to null on unmount, but Redux persists)
            // This catches switching between user stories with the same work product template
            const isNewContextOnRemount = isComponentRemount
                && activeContextKeyFromRedux != null
                && activeContextKeyFromRedux !== contextKey;

            console.log("Work product changed, clearing previous content", {
                oldId: currentWorkProductId,
                newId: workProduct?.id,
                isComponentRemount,
                isActualWorkProductChange,
                isNewContextOnRemount,
                contextKey,
                activeContextKeyFromRedux
            });

            // Clear all local state
            setTabContents({});
            setOriginalContent({});
            setSortedDocuments([]);
            setSectionHeader("");
            setSectionDescription("");
            setIsContentModified(false);
            setIsDirty(false);
            setContentVersion(prev => prev + 1);

            // Clear Redux state
            dispatch(setActiveEditorContent({ content: null }));
            dispatch(loadAllSections({ sections: [] }));
            dispatch(resetDocumentsState());

            // Reset tab if the context has changed (different WP, user story, feature, or project)
            // Do NOT clear previews — they persist until explicitly accepted or cancelled.
            // The multi-preview map scopes each preview to its own contextKey::sectionId.
            if (isActualWorkProductChange || isNewContextOnRemount) {
                dispatch(
                    setActiveTabRedux({
                        index: 0,
                        name: "",
                        sectionId: "",
                        contextKey
                    })
                );
            }

            // Update current work product ID
            if (workProduct?.id) {
                setCurrentWorkProductId(workProduct.id);

                // Trigger a fresh fetch for the new work product - only if we have valid IDs
                if (userProfile?.userid && wpId > 0 && actualProjectId > 0) {
                    const versionEntry = userProfile.settings?.ActiveVersion?.find((v: any) => v.ProjectId === actualProjectId && v.WorkProductId === wpId);
                    const hasPostRebuildLatestMarker = sessionStorage.getItem(latestVersionContextKey) === "1";
                    const shouldForceLatestVersion = Boolean(workProduct?.needs_rebuild) || hasPostRebuildLatestMarker;
                    const version = shouldForceLatestVersion ? 0 : versionEntry?.Version || 0;

                    if (hasPostRebuildLatestMarker) {
                        sessionStorage.removeItem(latestVersionContextKey);
                    }

                    setIsLoadingLatestRegeneratedVersion(shouldForceLatestVersion);

                    console.log("Fetching documents for work product:", {
                        wpId,
                        actualProjectId,
                        actualFeatureId,
                        actualUserStoryId,
                        version,
                        shouldForceLatestVersion
                    });

                    dispatch(
                        getWorkProductDocuments({
                            user_id: userProfile.userid,
                            project_id: actualProjectId,
                            workProduct_id: wpId,
                            version: version,
                            feature_id: actualFeatureId,
                            user_story_id: actualUserStoryId
                        })
                    ).catch(err => {
                        console.error("Failed to fetch documents for new work product:", err);
                    });
                } else {
                    console.warn("Cannot fetch documents - invalid IDs:", {
                        wpId,
                        actualProjectId,
                        userId: userProfile?.userid,
                        workProductId: workProduct?.id
                    });
                }
            }
        }
    }, [workProduct?.id, currentWorkProductId]);

    const diffObjectsWithChangeFlag = useCallback((obj1: any, obj2: any, path: string[] = []): boolean => {
        let hasChanges = false;

        if (!obj1 && !obj2) return false;
        if (!obj1 || !obj2) return true; // One exists, the other doesn't
        if (obj1 === obj2) return false;

        const keys1 = Object.keys(obj1 || {});
        const keys2 = Object.keys(obj2 || {});
        const allKeys = new Set([...keys1.map(k => k.toLowerCase()), ...keys2.map(k => k.toLowerCase())]);

        for (const lowerKey of allKeys) {
            const key1 = keys1.find(k => k.toLowerCase() === lowerKey);
            const key2 = keys2.find(k => k.toLowerCase() === lowerKey);

            const val1 = key1 ? obj1[key1] : undefined;
            const val2 = key2 ? obj2[key2] : undefined;
            const fullPath = [...path, lowerKey].join(".");

            const bothObjects =
                typeof val1 === "object" && val1 !== null && typeof val2 === "object" && val2 !== null && !Array.isArray(val1) && !Array.isArray(val2);

            if (bothObjects) {
                const subChange = diffObjectsWithChangeFlag(val1, val2, [...path, lowerKey]);
                //console.log(`🔄 Changed ${fullPath}:`, val1, "→", val2);
                if (subChange) hasChanges = true;
            } else {
                const normalizedVal1 = (val1 ?? "").toString().toLowerCase();
                const normalizedVal2 = (val2 ?? "").toString().toLowerCase();

                if (normalizedVal1 !== normalizedVal2) {
                    /*if (val1 === undefined) {
                        console.log(`➕ Added ${fullPath}:`, val2);
                    } else if (val2 === undefined) {
                        console.log(`❌ Removed ${fullPath}:`, val1);
                    } else {
                        console.log(`🔄 Changed ${fullPath}:`, val1, "→", val2);
                    }*/
                    hasChanges = true;
                }
            }
        }

        return hasChanges;
    }, []);

    // Removed - settings watching is handled by work product change effect above
    // This was causing infinite loops

    // Handle initial load and document processing - DISABLED - work product change effect handles all fetching
    // This effect was causing duplicate fetches
    /*useEffect(() => {
        // Only fetch if status is idle, we have a user profile, and we have a work product ID
        if (status === "idle" && userProfile?.userid && wpId > 0) {
            const versionEntry = userProfile.settings?.ActiveVersion?.find(
                (v: any) => v.ProjectId === actualProjectId && v.WorkProductId === wpId
            );
            const version = versionEntry?.Version || 0;

            console.log("DocSectionContainer: Initiating document fetch", {
                user_id: userProfile.userid,
                project_id: actualProjectId,
                workProduct_id: wpId,
                version,
                feature_id: actualFeatureId,
                user_story_id: actualUserStoryId,
                status
            });

            dispatch(
                getWorkProductDocuments({
                    user_id: userProfile.userid,
                    project_id: actualProjectId,
                    workProduct_id: wpId,
                    version: version,
                    feature_id: actualFeatureId,
                    user_story_id: actualUserStoryId
                })
            ).catch(err => {
                console.error("DocSectionContainer: Failed to fetch documents:", err);
            });
        } else {
            console.log("DocSectionContainer: Skipping document fetch", {
                status,
                hasUserProfile: !!userProfile?.userid,
                wpId,
                currentWorkProductId,
                matches: currentWorkProductId === wpId
            });
        }
    }, [status, wpId]);*/

    // Separate effect to process documents when they arrive
    useEffect(() => {
        console.log("DocSectionContainer: Document processing effect triggered", {
            status,
            documentsLength: documents?.length,
            documents: documents
        });

        if (status === "succeeded" || status === "failed") {
            setIsLoadingLatestRegeneratedVersion(false);
        }

        const activeContextMatchesCurrent =
            !!activeContext &&
            activeContext.workProductId === wpId &&
            activeContext.projectId === actualProjectId &&
            String(activeContext.featureId || "") === String(actualFeatureId || "") &&
            String(activeContext.userStoryId || "") === String(actualUserStoryId || "");

        if (status === "succeeded" && documents?.length > 0 && !activeContextMatchesCurrent) {
            console.warn("DocSectionContainer: Ignoring stale document response for previous context", {
                expected: {
                    workProductId: wpId,
                    projectId: actualProjectId,
                    featureId: actualFeatureId,
                    userStoryId: actualUserStoryId
                },
                received: activeContext
            });
            return;
        }

        if (status === "succeeded" && documents?.length > 0) {
            console.log("DocSectionContainer: Processing documents successfully");
            // Sort documents once and use consistently
            const sortedDocs = [...documents].sort((a, b) => a.sort_order - b.sort_order);
            setSortedDocuments(sortedDocs);

            // Check if we should restore a previously active section (e.g., one with a pending preview)
            // This preserves the user's context when returning from navigation
            let targetIndex = 0;
            let targetSection = sortedDocs[0];

            if (activeTabSectionIdFromRedux) {
                const previousSectionIndex = sortedDocs.findIndex(doc => String(doc.section_id) === activeTabSectionIdFromRedux);
                if (previousSectionIndex !== -1) {
                    targetIndex = previousSectionIndex;
                    targetSection = sortedDocs[previousSectionIndex];
                    console.log("Restoring previously active section:", {
                        sectionId: activeTabSectionIdFromRedux,
                        index: targetIndex,
                        title: targetSection.section_title
                    });
                }
            }

            setSectionHeader(targetSection.section_title);
            dispatch(
                setActiveTabRedux({
                    index: targetIndex,
                    name: targetSection.section_title,
                    sectionId: String(targetSection.section_id),
                    contextKey
                })
            );

            // Load all sections into documentContext for chat access
            const allSections = sortedDocs.map(doc => ({
                id: String(doc.section_id),
                title: doc.section_title,
                content: null, // Will be populated when editor loads
                markdown: doc.content || "",
                sort_order: doc.sort_order // Keep sort order for reference
            }));
            dispatch(loadAllSections({ sections: allSections }));

            setContentVersion(prev => prev + 1);
        } else if (status === "succeeded" && (!documents || documents.length === 0)) {
            console.warn("DocSectionContainer: Documents fetch succeeded but no documents returned", {
                wpId,
                actualProjectId,
                actualFeatureId,
                actualUserStoryId
            });
        } else if (status === "failed") {
            console.error("DocSectionContainer: Documents fetch failed", { error });
        }
    }, [status, documents, activeContext, wpId, actualProjectId, actualFeatureId, actualUserStoryId, refreshNonce]);

    // No longer needed - activeTab is controlled by parent component

    // Tab syncing is now handled by DocumentSectionsList component

    // Convert editor content to Markdown string
    const plateValueToMarkdown = (value: any | string): string => {
        if (typeof value === "string") return value;
        if (!Array.isArray(value)) return "";

        const render = (nodes: any[], isTop = false, listContext: { type?: "ul" | "ol"; depth: number } = { depth: 0 }): string =>
            nodes
                .map((node: any) => {
                    if (node.text != null) {
                        let t = node.text;
                        if (node.bold) t = `**${t}**`;
                        if (node.italic) t = `*${t}*`;
                        if (node.code) t = `\`${t}\``;
                        return t;
                    }

                    const child = render(node.children || [], false, listContext);
                    switch (node.type) {
                        case "h1":
                            return `# ${child}\n\n`;
                        case "h2":
                            return `## ${child}\n\n`;
                        case "h3":
                            return `### ${child}\n\n`;
                        case "p":
                            // Handle IndentListPlugin format
                            if (node.listStyleType && node.indent) {
                                const indent = "  ".repeat(node.indent);
                                const marker = node.listStyleType === "decimal" ? "1." : "-";
                                return `${indent}${marker} ${child}\n`;
                            }
                            return `${child}\n\n`;
                        case "ul": {
                            const newContext = { type: "ul" as const, depth: listContext.depth };
                            const listItems = render(node.children || [], false, newContext);
                            return `${listItems}${listContext.depth === 0 ? "\n" : ""}`;
                        }
                        case "ol": {
                            const newContext = { type: "ol" as const, depth: listContext.depth };
                            const listItems = render(node.children || [], false, newContext);
                            return `${listItems}${listContext.depth === 0 ? "\n" : ""}`;
                        }
                        case "li": {
                            const indent = "  ".repeat(listContext.depth);
                            const marker = listContext.type === "ol" ? "1." : "-";

                            // Handle nested lists within list items
                            const processedChild = render(node.children || [], false, { ...listContext, depth: listContext.depth + 1 });

                            return `${indent}${marker} ${processedChild}\n`;
                        }
                        case "blockquote":
                            return `> ${child}\n\n`;
                        case "code_block":
                            return `\`\`\`\n${child}\n\`\`\`\n\n`;

                        // NEW: table handling
                        case "table": {
                            const rows = (node.children || []).map((r: any, i: number) => {
                                const isHeaderRow = i === 0;
                                const cells = (r.children || []).map((c: any) => renderCell(c.children || [], { isHeader: isHeaderRow }));

                                const line = `| ${cells.join(" | ")} |`;
                                if (isHeaderRow) {
                                    const sep = `| ${cells.map(() => "---").join(" | ")} |`;
                                    return `${line}\n${sep}`;
                                }
                                return line;
                            });

                            // IMPORTANT: single trailing newline keeps block idempotent
                            return `${rows.join("\n")}\n`;
                        }
                        case "tr":
                            return render(node.children || [], false, listContext);
                        case "th":
                            return render(node.children || [], false, listContext);
                        case "td":
                            return render(node.children || [], false, listContext);

                        default:
                            return child;
                    }
                })
                // add a newline between top-level blocks to avoid "A123"
                .join(isTop ? "\n" : "");

        return render(value as any[], true)
            .replace(/\n{3,}/g, "\n\n") // collapse accidental triples
            .trimEnd();
    };

    // helper: inline-only render for table cells
    const renderCell = (nodes: any[], { isHeader = false } = {}): string => {
        const walk = (arr: any[]): string =>
            arr
                .map(n => {
                    if (n.text != null) {
                        let t = n.text;
                        if (n.bold) t = `**${t}**`;
                        if (n.italic) t = `*${t}*`;
                        if (n.code) t = `\`${t}\``;
                        return t;
                    }
                    const kids = walk(n.children || []);
                    switch (n.type) {
                        // treat block boundaries as a *single* soft break inside cells
                        case "p":
                        case "blockquote":
                        case "li":
                        case "h1":
                        case "h2":
                        case "h3":
                        case "ul":
                        case "ol":
                        case "code_block":
                            return kids ? `${kids}\n` : "";
                        default:
                            return kids;
                    }
                })
                .join("");

        // collapse any multi newlines to single newline
        let s = walk(nodes)
            .replace(/\n{2,}/g, "\n")
            .trim();

        // headers must stay one line
        if (isHeader) {
            s = s.replace(/\n+/g, " ");
        } else {
            // body cells: represent soft breaks in GFM with <br />
            s = s.replace(/\n/g, "<br />");
        }
        return s;
    };

    // Debounce function
    const debounce = <T extends (...args: any[]) => any>(func: T, wait: number) => {
        let timeout: NodeJS.Timeout;
        return (...args: Parameters<T>) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func(...args), wait);
        };
    };

    // Auto-save function
    const autoSave = useCallback(
        async (newValue: any | string, document: WorkproductDocument, force: boolean = false) => {
            if (!writeAllowedRef.current) return;
            // Only auto-save if content has been modified or force is true
            if (!isContentModified && !force) {
                return;
            }

            const contentToSave = typeof newValue === "string" ? newValue : plateValueToMarkdown(newValue);

            // Tiptap can emit its empty bootstrap document while a populated
            // warehouse document is still being hydrated (most often when
            // entering/leaving the editor with collaboration enabled). Never
            // let that lifecycle event overwrite the persisted artifact.
            // Explicit non-empty edits continue through the normal autosave.
            const persistedContent = originalContent[wpId]?.[activeTab] || document.content || "";
            if (!force && wouldErasePopulatedDocument(contentToSave, persistedContent)) {
                console.warn("Skipping empty editor autosave for populated document", {
                    warehouseId: document.warehouse_id,
                    sectionId: document.section_id
                });
                setIsContentModified(false);
                return;
            }

            console.log("Auto-save triggered:", {
                warehouseId: document.warehouse_id,
                sectionId: document.section_id,
                projectId: document.project_id,
                userId: document.user_id,
                content: contentToSave,
                timestamp: new Date().toISOString()
            });

            // Start saving
            setIsSaving(true);

            try {
                const result = await updateDocumentContent(document.warehouse_id, contentToSave, token || "");

                if (result.success) {
                    console.log("Document saved successfully:", result);
                    // Update original content to prevent re-saving unchanged content
                    // inside autoSave after a successful save
                    setOriginalContent(prev => ({
                        ...prev,
                        [wpId]: { ...(prev[wpId] ?? {}), [activeTab]: contentToSave }
                    }));

                    setIsContentModified(false);
                } else {
                    console.error("Failed to save document:", result.error);
                }
            } catch (error) {
                console.error("Error saving document:", error);
            } finally {
                setIsSaving(false);
            }
        },
        [isContentModified, token, activeTab, readOnly, originalContent, wpId]
    );

    // Create debounced auto-save
    const debouncedAutoSave = useMemo(
        () => debounce(autoSave, 2000), // 2 second delay
        [autoSave]
    );

    // Store original content when documents are loaded
    useEffect(() => {
        if (status === "succeeded" && documents?.length) {
            const originalsForWp: Record<number, string> = {};
            documents.forEach((doc, idx) => {
                originalsForWp[idx] = doc.content || "";
            });
            setOriginalContent(prev => ({ ...prev, [wpId]: originalsForWp }));
            setIsContentModified(false); // Reset modified flag when new documents are loaded
        }
    }, [status, documents]);

    // Removed - no longer needed since we're updating documents directly

    // Tab changes are now handled by parent component through props

    const handleEditorChange = (newValue: any) => {
        if (readOnly) return;
        dispatch(setActiveEditorContent(newValue));

        const currentMarkdown = typeof newValue === "string" ? newValue : plateValueToMarkdown(newValue);
        const originalMarkdown = originalContent[wpId]?.[activeTab] || "";
        const hasChanged = currentMarkdown.trim() !== originalMarkdown.trim();
        if (hasChanged && !isContentModified) setIsContentModified(true);

        // ✅ keep per-tab cache up to date so remounts use fresh content
        setTabContents(prev => ({
            ...prev,
            [wpId]: { ...(prev[wpId] ?? {}), [activeTab]: currentMarkdown }
        }));

        // Optional: also mirror into sortedDocuments so both sources stay fresh
        setSortedDocuments(prev => {
            if (!prev.length) return prev;
            const next = [...prev];
            next[activeTab] = { ...next[activeTab], content: currentMarkdown };
            return next;
        });

        if (documents && documents[activeTab]) {
            const currentDoc = documents[activeTab];
            dispatch(
                updateSectionContent({
                    id: String(currentDoc.section_id),
                    content: newValue,
                    markdown: currentMarkdown
                })
            );
            if (hasChanged) debouncedAutoSave(newValue, currentDoc);
        }

        if (adminEditorActive) setIsDirty(true);
    };

    // Remote peers' edits: refresh caches and Redux context, but never autosave —
    // the author's client persists its own edits.
    const handleRemoteEditorChange = (html: string) => {
        setTabContents(prev => ({
            ...prev,
            [wpId]: { ...(prev[wpId] ?? {}), [activeTab]: html }
        }));
        setSortedDocuments(prev => {
            if (!prev.length) return prev;
            const next = [...prev];
            next[activeTab] = { ...next[activeTab], content: html };
            return next;
        });
        if (documents && documents[activeTab]) {
            dispatch(
                updateSectionContent({
                    id: String(documents[activeTab].section_id),
                    content: html,
                    markdown: html
                })
            );
        }
        // Intentionally updated on every remote change (unlike autoSave, which only
        // updates originalContent after a successful save): under the shared Yjs
        // fragment all peers converge on the same content, so the remote-merged HTML
        // is the new "unmodified" baseline for this client's hasChanged comparison.
        setOriginalContent(prev => ({
            ...prev,
            [wpId]: { ...(prev[wpId] ?? {}), [activeTab]: html }
        }));
    };

    // A sync pull (Jira/Azure DevOps) wrote fresh ticket HTML to the section
    // this document has open. Refresh every local copy in place — otherwise the
    // stale in-memory/Yjs content autosaves right back over the pulled content
    // as soon as the user edits or reopens the document.
    const applyPulledContent = useCallback((sectionId: string, html: string) => {
        const idx = sortedDocuments.findIndex(d => String(d.section_id) === sectionId);
        if (idx === -1) return; // pulled section belongs to a different document
        // The pull already persisted this content — make it the saved baseline.
        setOriginalContent(prev => ({ ...prev, [wpId]: { ...(prev[wpId] ?? {}), [idx]: html } }));
        setTabContents(prev => ({ ...prev, [wpId]: { ...(prev[wpId] ?? {}), [idx]: html } }));
        setSortedDocuments(prev => {
            if (!prev.length) return prev;
            const next = [...prev];
            next[idx] = { ...next[idx], content: html };
            return next;
        });
        dispatch(updateSectionContent({ id: sectionId, content: html, markdown: html }));
        setIsContentModified(false);
        // Write into the live editor instance. In collab mode this is the only
        // correct channel: Yjs owns the content, so a local transaction carries
        // the pulled HTML to peers and the server-persisted ydoc — without it
        // the stale fragment resurfaces on reopen and autosaves over the pull.
        // In solo mode the value-reset effect would also cover the mounted tab,
        // but setContent keeps a single path for both.
        const liveEditor = getHostDocumentBridge().adapter.getEditorForSection(sectionId);
        if (liveEditor && !readOnly) {
            liveEditor.commands.setContent(html, true);
        }
    }, [sortedDocuments, wpId, dispatch, readOnly]);

    useEffect(() => {
        const onStoryContentUpdated = (e: Event) => {
            const detail = (e as CustomEvent).detail;
            if (!detail?.userStoryId || detail.userStoryId !== userStoryId) return;
            if (detail.descriptionHtml && detail.sectionId != null) {
                applyPulledContent(String(detail.sectionId), detail.descriptionHtml);
            }
            setRefreshNonce(n => n + 1);
        };
        window.addEventListener("storyContentUpdated", onStoryContentUpdated);
        return () => window.removeEventListener("storyContentUpdated", onStoryContentUpdated);
    }, [userStoryId, applyPulledContent]);

    // Handle rebuild confirmation - called when user clicks rebuild button in toolbar
    const handleRebuildConfirm = async () => {
        if (readOnly) return;
        if (!token || !projectId || !workProduct?.id) {
            console.error("Missing required data for rebuild:", { token: !!token, projectId, workProductId: workProduct?.id });
            setShowRebuildDialog(false);
            return;
        }

        setIsRebuilding(true);

        // Call onRebuildStart to set the generating state in parent (shows spinner on Feature Studio)
        if (onRebuildStart) {
            const wpIdStr = String(workProduct.id);
            onRebuildStart(wpIdStr, projectId, actualFeatureId || undefined, actualUserStoryId);
        }

        try {
            // Build job data similar to handleWorkProductGenerate in workProductHandlers.ts
            const workProductId = typeof workProduct.id === "string" ? parseInt(workProduct.id, 10) : workProduct.id;
            const jobData: any = {
                workproductid: workProductId,
                projectid: projectId,
                userid: userProfile?.userid,
                force_new_version: true
            };

            // Add featureid if available (feature level or below)
            if (actualFeatureId) {
                jobData.featureid = typeof actualFeatureId === "string" ? parseInt(actualFeatureId, 10) : actualFeatureId;
            }

            // Add user_story_id if available (user story level)
            if (actualUserStoryId) {
                jobData.user_story_id = String(actualUserStoryId);
            }

            console.log("Submitting rebuild job:", jobData);

            const wpIdStr = String(workProduct.id);

            // Submit the job
            await submitAndStreamJob(
                jobData,
                token,
                update => {
                    console.log("Rebuild job update:", update);
                    // Update progress in parent via callback
                    if (onProgressUpdate) {
                        onProgressUpdate(wpIdStr, projectId, actualFeatureId || undefined, actualUserStoryId, {
                            progress: update.progress,
                            message: update.message || "Processing..."
                        });
                    }
                },
                () => {
                    console.log("Rebuild job completed");
                    // Notify parent that rebuild is complete
                    if (onRebuildJobComplete) {
                        onRebuildJobComplete(wpIdStr, projectId, actualFeatureId || undefined, actualUserStoryId);
                    }
                },
                error => {
                    console.error("Rebuild job error:", error);
                    // Also call complete on error to clean up generating state
                    if (onRebuildJobComplete) {
                        onRebuildJobComplete(wpIdStr, projectId, actualFeatureId || undefined, actualUserStoryId);
                    }
                }
            );

            // Close dialog and navigate back to Feature Studio
            setShowRebuildDialog(false);
            setIsRebuilding(false);
            onRebuildComplete?.();
        } catch (error) {
            console.error("Failed to submit rebuild job:", error);
            setIsRebuilding(false);
            setShowRebuildDialog(false);
        }
    };

    // Check if work product has invalid ID - show helpful message
    if (wpId === 0 || !wpId || actualProjectId === 0) {
        return (
            <Box sx={{ p: 4, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                    Work Product Not Yet Saved
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 500, mt: 2 }}>
                    This work product needs to be generated or activated before you can edit its content. Please generate the work product first to create its
                    sections and content.
                </Typography>
            </Box>
        );
    }

    if (status === "loading") {
        return <WorkProductEditorSkeleton />;
    }

    if (status === "failed") {
        return (
            <Box sx={{ p: 4, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }}>
                <Typography variant="h6" color="error" gutterBottom>
                    Failed to Load Document
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 500, mt: 2 }}>
                    {error || "An error occurred while loading the document sections. Please try refreshing the page or contact support if the issue persists."}
                </Typography>
            </Box>
        );
    }
    if (status === "succeeded" && documents && documents.length === 0) {
        return <WorkProductEditorSkeleton message="No Documents Currently Available" />;
    }

    // If documents loaded but sortedDocuments not yet set, show loading
    if (status === "succeeded" && documents && sortedDocuments.length === 0) {
        return <WorkProductEditorSkeleton />;
    }

    return (
        <>
            {" "}
            {status === "succeeded" && sortedDocuments && sortedDocuments.length > 0 && (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                            height: "100%"
                        }}
                >
                    {isLoadingLatestRegeneratedVersion && (
                        <Alert severity="info" sx={{ mb: 1 }}>
                            Loading latest regenerated version...
                        </Alert>
                    )}
                    {adminEditorActive && (
                        <>
                            <Resizable
                                className="rounded-shadow-div"
                                size={{ height: topHeight }}
                                style={{ flexShrink: 0, width: "100%", background: "#eef" }}
                                enable={{
                                    top: false,
                                    right: false,
                                    bottom: true,
                                    left: false,
                                    topRight: false,
                                    bottomRight: false,
                                    bottomLeft: false,
                                    topLeft: false
                                }}
                                onResizeStop={(e, dir, ref, d) => {
                                    setTopHeight(Math.max(50, topHeight + d.height));
                                }}
                            >
                                <Box
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        overflow: "hidden",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center"
                                    }}
                                >
                                    {/* PromptEditor component - placeholder */}
                                    <Typography color="textSecondary">Prompt Editor</Typography>
                                </Box>
                            </Resizable>
                            <div
                                style={{
                                    height: 2,
                                    cursor: "row-resize",
                                    background: "#ccc"
                                }}
                            />
                        </>
                    )}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            width: "100%", // Take full width of parent
                            height: "100%", // Take full height of parent
                            overflow: "hidden" // Prevent content overflow
                        }}
                    >
                        {/* Editor Container - Static sizing within bounds */}
                        <Box
                            sx={{
                                display: "flex",
                                width: "100%",
                                flexDirection: "column",
                                flex: "1 1 0", // Take remaining space but enforce constraints
                                minHeight: 0, // Critical for flex child to shrink
                                maxHeight: "100%", // Prevent overflow beyond container
                                overflow: "hidden"
                            }}
                        >
                            <Card
                                key="editArea"
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    flex: "1 1 0", // Static flex sizing
                                    minHeight: 0, // Allow shrinking
                                    maxHeight: "100%", // Constrain height
                                    borderRadius: 0,
                                    border: "none",
                                    boxShadow: 0,
                                    overflow: "hidden"
                                }}
                            >
                                <Box
                                    sx={{
                                        flex: "1 1 0", // Static flex sizing
                                        minHeight: 0, // Allow shrinking
                                        maxHeight: "100%", // Constrain height
                                        overflow: "hidden",
                                        display: "flex",
                                        flexDirection: "column"
                                    }}
                                >
                                    {sortedDocuments && sortedDocuments[activeTab] && activeTab < sortedDocuments.length && (
                                        <Box
                                            sx={{
                                                flex: "1 1 0",
                                                minHeight: 0,
                                                maxHeight: "100%",
                                                overflow: "hidden",
                                                display: "flex",
                                                flexDirection: "column"
                                            }}
                                        >
                                            {(() => {
                                                // Use either the accepted content for this tab or the original document content
                                                // const editorValue = tabContents[wpId]?.[activeTab] ?? sortedDocuments[activeTab].content;

                                                // Include document ID to remount when document changes
                                                // This is necessary because Plate editor deserializes markdown on mount only
                                                const docId = sortedDocuments?.[activeTab]?.section_id ?? "new";
                                                const editorKey = `wp-${wpId}-tab-${activeTab}-doc-${docId}-wh-${sortedDocuments[activeTab]?.warehouse_id ?? "na"}-${collabForEditor ? "collab" : "solo"}`;

                                                // Use the markdown preview if available, otherwise convert Plate content
                                                const previewMarkdownContent = previewMarkdown || (previewContent ? plateValueToMarkdown(previewContent) : "");

                                                return (
                                                    <EditorWithOverlay
                                                        editorKey={editorKey}
                                                        sectionHeader={sectionHeader ?? "Untitled Section"}
                                                        sectionDescription={sectionDescription ?? "No description available"}
                                                        // editorValue={editorValue}
                                                        editorValue={derivedEditorValue}
                                                        handleEditorChange={handleEditorChange}
                                                        collab={collabForEditor}
                                                        onRemoteChange={collabForEditor ? handleRemoteEditorChange : undefined}
                                                        showPreview={showPreviewForSection}
                                                        previewContent={previewMarkdownContent}
                                                        isStreaming={isStreaming}
                                                        needsRebuild={sortedDocuments[activeTab]?.needs_rebuild}
                                                        workProduct={workProduct}
                                                        projectId={projectId}
                                                        featureId={featureId}
                                                        userStoryId={actualUserStoryId}
                                                        sectionId={activeSectionKey}
                                                        workProductTitle={wpTitle}
                                                        hasJiraLink={hasJiraLink}
                                                        hasAzureDevOpsLink={hasAzureDevOpsLink}
                                                        workProductId={wpId}
                                                        allSections={allSections}
                                                        onNavigateToIntegrations={onNavigateToIntegrations}
                                                        readOnly={readOnly}
                                                        presenceProvider={collabSession?.status === "connected" ? collabSession.provider : undefined}
                                                        presenceSectionId={activeDoc?.warehouse_id}
                                                        onAccept={() => {
                                                            if (readOnly) return;
                                                            if (previewMarkdown || previewContent) {
                                                                // Use markdown preview directly if available
                                                                const updatedContent = previewMarkdown || plateValueToMarkdown(previewContent!);

                                                                // Update the content for this specific tab
                                                                setTabContents(prev => ({
                                                                    ...prev,
                                                                    [wpId]: { ...(prev[wpId] ?? {}), [activeTab]: updatedContent }
                                                                }));

                                                                // Force re-render by incrementing version
                                                                setContentVersion(prev => prev + 1);

                                                                // Clear this specific preview
                                                                dispatch(cancelPreview({ contextKey, sectionId: sectionIdKey || "" }));

                                                                // Update editor content in Redux if we have Plate content
                                                                if (previewContent && Array.isArray(previewContent)) {
                                                                    dispatch(setActiveEditorContent(previewContent));
                                                                }

                                                                // Mark as dirty to trigger save
                                                                setIsDirty(true);

                                                                if (collabForEditor) {
                                                                    // Collab mode: TiptapEditor's value-reset effect is disabled
                                                                    // (Yjs owns content), so a forced autoSave here would persist
                                                                    // HTML the shared ydoc never saw — the two would diverge for
                                                                    // peers. Instead apply the accepted content directly to the
                                                                    // live editor instance: this becomes a local Yjs transaction
                                                                    // (propagates to peers via the fragment) and flows through the
                                                                    // editor's normal onChange -> autosave path with content that
                                                                    // the ydoc actually contains.
                                                                    const liveEditor = getHostDocumentBridge().adapter.getEditorForSection(activeSectionKey ?? "");
                                                                    if (liveEditor) {
                                                                        const html = renderMarkdownToHtml(updatedContent);
                                                                        liveEditor.commands.setContent(html, true);
                                                                        // Force-persist the HTML projection now: the debounced
                                                                        // onChange autosave can no-op on a stale isContentModified
                                                                        // closure for a one-shot programmatic change. The editor's
                                                                        // HTML matches the ydoc after setContent, so no divergence.
                                                                        if (documents && documents[activeTab]) {
                                                                            autoSave(liveEditor.getHTML(), documents[activeTab], true);
                                                                        }
                                                                    } else {
                                                                        console.warn("Preview accept in collab mode: no live editor registered for this section; skipping to avoid HTML/ydoc divergence");
                                                                    }
                                                                } else if (documents && documents[activeTab]) {
                                                                    // Solo mode: safe to force autoSave directly.
                                                                    console.log("Content accepted from chat preview, triggering auto-save");
                                                                    setIsContentModified(true); // Mark as modified
                                                                    autoSave(previewContent || updatedContent, documents[activeTab], true);
                                                                }
                                                            }
                                                        }}
                                                        onCancel={() => {
                                                            dispatch(cancelPreview({ contextKey, sectionId: sectionIdKey || "" }));
                                                        }}
                                                        onRebuild={canRebuild ? () => {
                                                            if (readOnly) return;
                                                            console.log("Rebuild requested for work product:", wpId);
                                                            setShowRebuildDialog(true);
                                                        } : undefined}
                                                    />
                                                );
                                            })()}
                                        </Box>
                                    )}
                                </Box>
                            </Card>
                        </Box>
                    </Box>
                </Box>
            )}
            {/* Rebuild Confirmation Dialog */}
            <Dialog
                open={showRebuildDialog}
                onClose={() => !isRebuilding && setShowRebuildDialog(false)}
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        minWidth: 400,
                        maxWidth: 500
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        bgcolor: "#FFF3E0",
                        color: "#E65100",
                        borderBottom: "1px solid #FFE0B2"
                    }}
                >
                    <WarningIcon sx={{ color: "#EF6C00" }} />
                    Rebuild Work Product
                </DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 2 }}>
                    <Typography>Rebuilding will overwrite your changes and regenerate this Work Product using the latest inputs. Continue?</Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setShowRebuildDialog(false)}
                        disabled={isRebuilding}
                        variant="outlined"
                        sx={{
                            color: "#666",
                            borderColor: "#ccc",
                            "&:hover": {
                                borderColor: "#999",
                                bgcolor: "#f5f5f5"
                            }
                        }}
                    >
                        No
                    </Button>
                    <Button
                        onClick={handleRebuildConfirm}
                        disabled={isRebuilding}
                        variant="contained"
                        sx={{
                            bgcolor: "#EF6C00",
                            "&:hover": {
                                bgcolor: "#E65100"
                            }
                        }}
                    >
                        {isRebuilding ? "Rebuilding..." : "Yes"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

interface EditorWithOverlayProps {
    editorKey: string | number;
    sectionHeader?: string;
    sectionDescription?: string;
    editorValue: any; // Adjust this based on your PlateEditor value type
    handleEditorChange: (value: any) => void; // Adjust value type accordingly
    collab?: TiptapCollab;
    onRemoteChange?: (html: string) => void;
    previewContent: string;
    showPreview: boolean;
    onAccept: () => void;
    onCancel: () => void;
    isStreaming?: boolean;
    needsRebuild?: boolean;
    onRebuild?: () => void;
    workProduct?: any;
    projectId?: number;
    featureId?: string;
    userStoryId?: string;
    sectionId?: number;
    workProductTitle?: string;
    hasJiraLink?: boolean;
    hasAzureDevOpsLink?: boolean;
    workProductId?: number;
    allSections?: any[];
    onNavigateToIntegrations?: () => void;
    readOnly?: boolean;
    presenceProvider?: TiptapCollab["provider"];
    presenceSectionId?: number | string;
}

const EditorWithOverlay: React.FC<EditorWithOverlayProps> = ({
    editorKey,
    sectionHeader,
    sectionDescription,
    editorValue,
    handleEditorChange,
    collab,
    onRemoteChange,
    previewContent,
    showPreview,
    onAccept,
    onCancel,
    isStreaming = false,
    needsRebuild = false,
    onRebuild,
    workProduct,
    projectId,
    featureId,
    userStoryId,
    sectionId,
    workProductTitle,
    hasJiraLink,
    hasAzureDevOpsLink,
    workProductId,
    allSections,
    onNavigateToIntegrations,
    readOnly = true,
    presenceProvider,
    presenceSectionId
}) => {
    return (
        <Box
            position="relative"
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column"
            }}
        >
            {/* Out of Date Alert */}
            {needsRebuild && (
                <Alert
                    severity="warning"
                    icon={<RefreshIcon />}
                    sx={{
                        mb: 1,
                        borderRadius: 1,
                        "& .MuiAlert-message": {
                            width: "100%"
                        }
                    }}
                >
                    This Work Product may be out of date. Refresh to apply the latest updates.
                </Alert>
            )}

            {!editorValue || !sectionHeader ? (
                <Box
                    sx={{
                        flex: showPreview ? "0 0 70%" : "1",
                        overflow: "hidden",
                        p: 2
                    }}
                >
                    <Skeleton variant="rectangular" width="100%" height="100%" />
                </Box>
            ) : (
                <Box sx={{ flex: showPreview ? "0 0 70%" : "1", display: "flex", flexDirection: "column", minHeight: 0 }}>
                    <TiptapEditor
                        key={editorKey}
                        title={sectionHeader ?? "Untitled Section"}
                        description={sectionDescription ?? "No description available"}
                        value={editorValue}
                        onChange={handleEditorChange}
                        collab={collab}
                        onRemoteChange={onRemoteChange}
                        projectId={projectId}
                        featureId={featureId}
                        userStoryId={userStoryId}
                        sectionId={sectionId}
                        workProductTitle={workProductTitle}
                        hasJiraLink={hasJiraLink}
                        hasAzureDevOpsLink={hasAzureDevOpsLink}
                        needsRebuild={needsRebuild}
                        onRebuild={onRebuild}
                        workProductId={workProductId}
                        allSections={allSections}
                        onNavigateToIntegrations={onNavigateToIntegrations}
                        readOnly={readOnly}
                        presenceProvider={presenceProvider}
                        presenceSectionId={presenceSectionId}
                    />
                </Box>
            )}

            {showPreview && (
                <Paper
                    elevation={8}
                    sx={{
                        height: "30%",
                        bgcolor: "background.paper",
                        display: "flex",
                        flexDirection: "column",
                        borderTop: "2px solid",
                        borderColor: "divider",
                        p: 2,
                        overflow: "hidden"
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2
                        }}
                    >
                        <Typography variant="h6" color="primary">
                            Modification Preview
                        </Typography>
                        <Box display="flex" gap={1}>
                            <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={onAccept}
                                disabled={isStreaming || readOnly}
                            >
                                Accept
                            </Button>
                            <Button variant="outlined" color="error" size="small" onClick={onCancel}>
                                Cancel
                            </Button>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            flex: 1,
                            overflowY: "auto",
                            bgcolor: "grey.50",
                            borderRadius: 1,
                            p: 2
                        }}
                    >
                        <ReactMarkdown>{previewContent}</ReactMarkdown>
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

interface WorkProductEditorSkeletonProps {
    message?: string;
    size?: {
        width: number | string;
        height: number | string;
    };
}

const WorkProductEditorSkeleton: React.FC<WorkProductEditorSkeletonProps> = ({ message }) => {
    return (
        <Box sx={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    border: "1px solid #ccc",
                    overflow: "auto",
                    width: "100%",
                    height: "100%"
                }}
            >
                {/* Tabs skeleton removed - tabs are now in parent component */}

                {/* Editor Skeleton */}
                <Box
                    sx={{
                        display: "flex",
                        width: "100%",
                        flexDirection: "column",
                        flex: "1 1 0",
                        minHeight: 0,
                        overflow: "hidden"
                    }}
                >
                    <Card
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            flex: "1 1 0",
                            minHeight: 0,
                            borderRadius: 0,
                            border: "none",
                            boxShadow: 0,
                            overflow: "hidden"
                        }}
                    >
                        {!message ? (
                            <Box sx={{ flex: "1 1 0", minHeight: 0, p: 2, overflow: "auto" }}>
                                <Skeleton height={30} width="60%" sx={{ mb: 1 }} />
                                <Skeleton height={24} width="40%" sx={{ mb: 2 }} />
                                {Array.from({ length: 10 }).map((_, i) => (
                                    <Skeleton key={i} height={16} width={`${90 - i * 5}%`} sx={{ mb: 1 }} />
                                ))}
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    flex: "1 1 0",
                                    minHeight: 0,
                                    p: 2,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    textAlign: "center"
                                }}
                            >
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    Select a Work Product from the left.
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Once generated, it will appear here.
                                </Typography>
                            </Box>
                        )}
                    </Card>
                </Box>
            </Box>
        </Box>
    );
};

export default DocSectionContainer;
